import { saysWhere } from "@rally/agreed";
import { breakpoints, Col, Icon, Picture, Tappable, Text, useTheme } from "@rally/ui-kit";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { failureSaid } from "../../localization/failure.ts";
import { say } from "../../localization/say.ts";
import { WhenLabel } from "../../localization/when-label.tsx";
import type { PersonInConversation, ReadFile } from "../../net/api.ts";
import { act, read } from "../../net/rally.ts";
import { coalesced } from "../activity-refresh.ts";
import { SourceCode } from "../app/source-code.tsx";
import { Composer, type SubmissionIdentity } from "../composer/composer.tsx";
import type { Candidate } from "../composer/mentions.ts";
import { sameReplyTarget } from "../composer/submission.ts";
import { type ListedFile, refreshFilesIn, useOpened } from "../filed.ts";
import { Marked } from "../message/marked.tsx";
import { canonicalTypedPayload } from "../message/message-payload.ts";
import { useFocusChrome } from "../panel/panel-focus.tsx";
import { usePanelActions, useStack } from "../panel/panels.tsx";
import type { Card } from "../panel/stacking.ts";
import { titleOf } from "../panel/title.ts";
import { useLive } from "../run/live.ts";
import { TargetActions } from "../target-actions.tsx";
import { useAttaching } from "./attaching.ts";
import { commentsAreDrawn, fileSourcePath } from "./file-focus.ts";
import { isPdfPreview, linesIn, textPreviewFor } from "./file-preview.ts";
import { drawsInline, glyphFor, sizeSaid, typeSaid } from "./filing.ts";

const TEXT_PREVIEW_BYTES = 64 * 1024;
const PdfPreview = lazy(() => import("./pdf-preview.tsx").then((loaded) => ({ default: loaded.PdfPreview })));
export function fileCard(file: ListedFile, conversation: string): Card {
  return {
    key: `file:${file.id}`,
    title: file.name,
    render: () => <FileResource file={file} conversation={conversation} />,
  };
}

function useTextPreview(file: ListedFile, url: string | undefined): string | null {
  const text = textPreviewFor(file) !== null;
  const [held, setHeld] = useState<{ id: string; text: string | null }>({ id: file.id, text: null });
  useEffect(() => {
    if (!url || !text) return;
    let alive = true;
    void fetch(url, { headers: { range: `bytes=0-${TEXT_PREVIEW_BYTES - 1}` } })
      .then((answer) => (answer.ok ? answer.text() : null))
      .then((body) => {
        if (alive) setHeld({ id: file.id, text: body });
      })
      .catch(() => {
        if (alive) setHeld({ id: file.id, text: null });
      });
    return () => {
      alive = false;
    };
  }, [file.id, text, url]);
  return held.id === file.id ? held.text : null;
}

function InteractiveHtml({ file, focused }: { file: ListedFile; focused: boolean }) {
  const theme = useTheme();
  const previewUrl = useOpened(file.id, "preview");
  if (!previewUrl) return null;
  return (
    <iframe
      title={file.name}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      src={previewUrl}
      data-testid="file-html-preview"
      style={{
        display: "block",
        width: "100%",
        height: focused ? "calc(100vh - 152px)" : 420,
        border: `1px solid ${theme.color["border.default"]}`,
        borderRadius: theme.radius.md,
        background: "transparent",
      }}
    />
  );
}

function HeaderMark({ file }: { file: ListedFile }) {
  const theme = useTheme();
  const url = useOpened(file.id, "thumb");
  if (drawsInline(file) && url) {
    return (
      <Picture
        source={{ uri: url }}
        resizeMode="cover"
        style={{ width: 40, height: 40, borderRadius: theme.radius.md }}
      />
    );
  }
  return (
    <Col
      style={{
        width: 40,
        height: 40,
        borderRadius: theme.radius.md,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.color["bg.surfaceAlt"],
      }}
    >
      <Icon name={glyphFor(file)} size={18} color="text.secondary" />
    </Col>
  );
}

function ViewChoice({
  view,
  onChange,
}: {
  view: "preview" | "source";
  onChange: (view: "preview" | "source") => void;
}) {
  const theme = useTheme();
  return (
    <Col style={{ flexDirection: "row", gap: theme.space[1], flexShrink: 0 }}>
      {(["preview", "source"] as const).map((choice) => {
        const selected = choice === view;
        return (
          <Tappable
            key={choice}
            onPress={() => onChange(choice)}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            testID={`file-view-${choice}`}
            style={{
              minHeight: 26,
              justifyContent: "center",
              paddingHorizontal: theme.space[2],
              borderRadius: theme.radius.sm,
              backgroundColor: selected ? theme.color.accentSoft : "transparent",
            }}
          >
            <Text typeName="caption" color={selected ? "accent" : "text.tertiary"}>
              {say(choice === "preview" ? "files.preview" : "files.source")}
            </Text>
          </Tappable>
        );
      })}
    </Col>
  );
}

function useFileDiscussion(file: string, conversation: string) {
  const [resource, setResource] = useState<ReadFile | null>(null);
  const [people, setPeople] = useState<PersonInConversation[]>([]);
  const [title, setTitle] = useState<string | null>(null);
  const again = useCallback(() => {
    void read<ReadFile>("file.read", { file })
      .then(setResource)
      .catch(() => setResource(null));
  }, [file]);
  useEffect(() => {
    setResource(null);
    again();
    void read<PersonInConversation[]>("conversation.people", { conversation })
      .then(setPeople)
      .catch(() => setPeople([]));
    void read<{ title: string | null }>("conversation.head", { conversation })
      .then((head) => setTitle(head.title?.trim() || null))
      .catch(() => setTitle(null));
  }, [conversation, again]);
  const refresh = useMemo(() => coalesced(again), [again]);
  useEffect(() => () => refresh.cancel(), [refresh]);
  useLive((notice) => {
    if (notice.conversation === conversation) refresh.notice();
  });
  return { resource, people, title, again };
}

function Comment({
  comment,
  name,
  onReply,
  onChanged,
}: {
  comment: ReadFile["comments"][number];
  name: string;
  onReply: () => void;
  onChanged: () => void;
}) {
  const theme = useTheme();
  return (
    <Col style={{ gap: theme.space[1] }} testID={`file-comment-${comment.id}`}>
      <Col style={{ flexDirection: "row", alignItems: "baseline", gap: theme.space[2] }}>
        <Text typeName="label" color="text.primary">
          {name}
        </Text>
        <WhenLabel at={comment.at} />
        {comment.anchor ? (
          <Text typeName="caption" color="text.tertiary">
            {saysWhere(comment.anchor)}
          </Text>
        ) : null}
      </Col>
      {comment.answering ? (
        <Col
          testID={`file-comment-quote-${comment.id}`}
          style={{
            alignSelf: "flex-start",
            maxWidth: "100%",
            borderLeftWidth: 2,
            borderLeftColor: theme.color["border.strong"],
            paddingLeft: theme.space[2],
          }}
        >
          <Text
            typeName="caption"
            color="text.tertiary"
            numberOfLines={1}
          >{`“${comment.answering.said}”`}</Text>
        </Col>
      ) : null}
      <Marked text={comment.text} who={[]} />
      <TargetActions
        target={{ kind: "message", id: comment.id }}
        reactions={comment.reactions}
        away={comment.away}
        capabilities={comment.capabilities}
        onReply={onReply}
        onChanged={onChanged}
        testID={`file-comment-actions-${comment.id}`}
      />
    </Col>
  );
}

export function FileResource({ file, conversation }: { file: ListedFile; conversation: string }) {
  const theme = useTheme();
  const stack = useStack();
  const phone = theme.isAtMost(breakpoints.phone);
  const focused = stack.focused;
  const url = useOpened(file.id);
  const preview = useTextPreview(file, url);
  const kind = textPreviewFor(file);
  const pdf = isPdfPreview(file);
  const rich = kind === "markdown" || kind === "html";
  const lines =
    kind !== null && preview !== null && file.bytes <= TEXT_PREVIEW_BYTES ? linesIn(preview) : null;
  const metadata = [
    typeSaid(file),
    sizeSaid(file.bytes),
    ...(lines === null ? [] : [say("files.lines")(lines)]),
  ].join(" · ");
  const open = () => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };
  const download = () => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    link.rel = "noopener";
    link.click();
  };
  usePanelActions([
    ...(!phone && !focused
      ? [
          {
            key: "focus",
            icon: "maximize" as const,
            label: say("files.focus"),
            testID: "file-focus",
            onPress: stack.focus,
          },
        ]
      : []),
    {
      key: "download",
      icon: "download",
      label: say("files.download"),
      testID: "file-download",
      disabled: !url,
      onPress: download,
    },
    {
      key: "open",
      icon: "external-link",
      label: say("files.open"),
      testID: "file-open",
      disabled: !url,
      onPress: open,
    },
  ]);
  const [view, setView] = useState<"preview" | "source">("preview");
  const [dimensions, setDimensions] = useState<string | null>(null);
  const [pdfPages, setPdfPages] = useState<number | null>(null);
  const { resource, people, title, again } = useFileDiscussion(file.id, conversation);
  const conversationLabel =
    title ??
    (people.length > 0 && stack.me ? titleOf({ title: null, people }, stack.me) : say("files.conversation"));
  const fact =
    lines !== null
      ? { key: "lines", label: say("files.lines")(lines) }
      : dimensions
        ? { key: "dimensions", label: dimensions }
        : pdfPages !== null
          ? { key: "pages", label: say("files.pages")(pdfPages) }
          : null;
  useFocusChrome({
    size: sizeSaid(file.bytes),
    facts: fact ? [fact] : [],
    source: {
      label: conversationLabel,
      open: () => stack.leaveFocus(fileSourcePath(conversation, file.message)),
    },
    choice: rich && preview !== null ? { view, change: setView } : undefined,
  });
  const [commentsOpen, setCommentsOpen] = useState(false);
  useEffect(() => {
    if (focused) setCommentsOpen(false);
  }, [focused]);
  const [replying, setReplying] = useState<{ id: string; said: string } | null>(null);
  const latestReply = useRef(replying);
  latestReply.current = replying;
  const [focusComposer, setFocusComposer] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const attaching = useAttaching(conversation);
  const names = useMemo(
    () => new Map(people.map((person) => [person.account, person.displayName ?? person.handle])),
    [people],
  );
  const candidates: Candidate[] = people
    .filter((person) => person.removedAt === null)
    .map((person) => ({
      account: person.account,
      handle: person.handle,
      displayName: person.displayName,
      avatar: person.avatar,
      kind: person.agent ? "agent" : "person",
      here: true,
      role: person.role,
    }));
  const post = async (text: string, identify: SubmissionIdentity): Promise<boolean> => {
    const sentReply = replying;
    const parent = sentReply
      ? { kind: "message" as const, id: sentReply.id }
      : { kind: "file" as const, id: file.id };
    const carried = await attaching.ready();
    if (!text && carried.length === 0) return false;
    try {
      const files = carried.map(({ key, name, mediaType }) => ({ key, name, mediaType }));
      const submission = identify(canonicalTypedPayload({ conversation, text, replyTo: parent, files }));
      await act("message.post", {
        conversation,
        text,
        submission,
        replyTo: parent,
        ...(files.length > 0 ? { files } : {}),
      });
      attaching.taken(carried);
      setError(null);
      if (sameReplyTarget(sentReply, latestReply.current)) setReplying(null);
      again();
      refreshFilesIn(conversation);
      return true;
    } catch (cause) {
      attaching.retry(carried);
      setError(failureSaid(cause, say("files.commentError")));
      return false;
    }
  };

  return (
    <Col style={{ gap: theme.space[4] }} testID={`file-resource-${file.id}`}>
      <Col
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: theme.space[3],
          display: focused ? "none" : "flex",
        }}
      >
        <HeaderMark file={file} />
        <Col
          testID="file-metadata"
          style={{ flex: 1, minWidth: 0, flexDirection: "row", alignItems: "center", gap: 4 }}
        >
          <Text
            typeName="caption"
            color="text.tertiary"
            numberOfLines={1}
            style={{ minWidth: 0, flexShrink: 1 }}
          >{`${metadata} ·`}</Text>
          <WhenLabel at={file.at} numberOfLines={1} style={{ flexShrink: 0 }} />
        </Col>
        {rich && preview !== null && !focused ? <ViewChoice view={view} onChange={setView} /> : null}
      </Col>

      {pdf && url ? (
        <Suspense fallback={null}>
          <PdfPreview url={url} name={file.name} onPages={setPdfPages} />
        </Suspense>
      ) : null}
      {drawsInline(file) && url ? (
        <Picture
          source={{ uri: url }}
          accessibilityLabel={file.name}
          resizeMode="contain"
          onLoad={(event) => {
            const { width, height } = event.nativeEvent.source;
            if (width > 0 && height > 0) setDimensions(`${width} × ${height}`);
          }}
          style={{ width: "100%", height: 360, borderRadius: theme.radius.md }}
        />
      ) : null}
      {preview !== null && kind === "markdown" && view === "preview" ? (
        <Marked text={preview} who={[]} />
      ) : null}
      {preview !== null && kind === "html" ? (
        <Col style={{ display: view === "preview" ? "flex" : "none" }}>
          <InteractiveHtml file={file} focused={focused} />
        </Col>
      ) : null}
      {preview !== null && (kind === "source" || rich) ? (
        <Col style={{ display: kind === "source" || view === "source" ? "flex" : "none" }}>
          <SourceCode file={file} text={preview} />
        </Col>
      ) : null}

      {resource ? (
        <TargetActions
          target={{ kind: "file", id: file.id }}
          reactions={resource.reactions}
          away={resource.away}
          capabilities={resource.capabilities}
          onReply={() => {
            setReplying(null);
            setFocusComposer((was) => was + 1);
          }}
          onChanged={again}
          testID={`file-actions-${file.id}`}
        />
      ) : null}

      {focused ? (
        <Tappable
          accessibilityRole="button"
          accessibilityState={{ expanded: commentsOpen }}
          testID="file-comments-toggle"
          onPress={() => setCommentsOpen((open) => !open)}
          style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}
        >
          <Icon name={commentsOpen ? "chevron-down" : "chevron-up"} size={16} color="text.tertiary" />
          <Text typeName="label" color="text.secondary">
            {say("files.commentsDrawer")}
          </Text>
        </Tappable>
      ) : null}
      <Col
        style={{
          gap: theme.space[3],
          display: commentsAreDrawn(focused, commentsOpen) ? "flex" : "none",
        }}
        testID="file-comments"
      >
        {resource?.comments.length ? (
          resource.comments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              name={names.get(comment.by) ?? say("chat.someone")}
              onReply={() => setReplying({ id: comment.id, said: comment.text })}
              onChanged={again}
            />
          ))
        ) : (
          <Text typeName="caption" color="text.tertiary">
            {say("files.commentsEmpty")}
          </Text>
        )}

        <Composer
          conversation={conversation}
          draftKey={
            replying
              ? `${conversation}:file:${file.id}:reply:${replying.id}`
              : `${conversation}:file:${file.id}`
          }
          placeholder={replying ? undefined : say("files.comment")}
          testID="file-comment-composer"
          replyContext={replying ? { said: replying.said, onClose: () => setReplying(null) } : undefined}
          error={error}
          focusSignal={focusComposer}
          who={candidates}
          files={[file]}
          picked={attaching.picked}
          onPick={attaching.take}
          onDrop={attaching.drop}
          onSay={(text, _from, identify) => post(text, identify)}
        />
      </Col>
    </Col>
  );
}
